import React, {PropsWithChildren, ReactElement} from 'react';
import {Drawer, Theme} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {makeStyles} from 'tss-react/mui';
import filter from 'ramda/es/filter';
import values from 'ramda/es/values';
import {useSelector} from 'react-redux';
import {useLanguage} from '../../i18n';
import {BgLayerSubGroup, isVisible, LayerCollection, LayerGroup} from '../../model/layer/layer';
import {ApplicationState, useActionCreator} from '../../store';
import {toggleLayerGroup, turnOffBackgroundLayers} from '../../store/map/actions';
import {IconButton} from '../themeButtton/themeButton';
import classnames from 'classnames';
import './layerSelector.scss';
import {ToggleSwitch} from '../toggleSwitch/toggleSwitch';
import {useMobileBreakpointInnsyn} from '../../hooks/breakpoint';
import {Tooltip} from '../layerSelectorToolTip/tooltip';


const useLSStyles = makeStyles()((theme: Theme) =>
    {
        return {
            title: {
                backgroundColor: theme.palette.secondary.main,
            },
            imageButton: {
                '& img': {
                    border: `3px solid ${theme.palette.grey[300]}`,
                },
                '&:focus': {
                    fontWeight: 'bold',
                },
                '&:focus img': {
                    outline: `1px solid ${theme.palette.primary.dark}`,
                    outlineOffset: '2px',
                }
            },
            layerChecked: {
                '& img': {
                    border: `3px solid ${theme.palette.primary.light}`,
                }
            },
            imageSwitch: {
                '&:focus-within': {
                    outline: `1px solid ${theme.palette.primary.light}`,
                }
            },
            switchButtonWrapper: {
                display: 'flex',
                maxWidth: 'fit-content',
                '&:focus-within': {
                    outline: `1px solid ${theme.palette.primary.light}`,
                }
            }
        }
    });

const layerCollectionNotEmpty = (layers: LayerCollection): boolean => values(layers).length > 0;

type LayerSelectorProps = {
    open: boolean;
    closeDrawer: () => void;
};

export function LayerSelector(props: LayerSelectorProps): React.ReactElement {
    const { classes } = useLSStyles();
    const titleClass = classnames(classes.title, 'layer-selector-header');
    const mobile = useMobileBreakpointInnsyn();
    const layers = useSelector((state: ApplicationState) => state.map.layers);
    const l = useLanguage();
    const getLayerGroup = (ids: string[]): LayerCollection => filter(layer => ids.includes(layer.id), layers);
    const getBgLayerSubGroup = (id: BgLayerSubGroup): LayerCollection => filter(layer => layer.subGroup === id, layers);
    const drawerPosition = mobile ? 'bottom' : 'right';
    const toggleLayers = useActionCreator(toggleLayerGroup);
    
    // Layer groups
    const reinbeitedistriktLayer = getLayerGroup(['reinbeite_omr', 'reinbeite_distr']);
    const beitelagsLayer = getLayerGroup(['beitelagnamn', 'beitelaggrenser']);
    const samebyLayer = getLayerGroup(['samebygrenser']);
    const topoLayers = getBgLayerSubGroup(BgLayerSubGroup.topo);

    // hvis enten svensk eller norsk kart er skrudd av så skal man kunne velge å kun se basiskartet fordi sverige-kartet overlapper norge
    const showBasisLayerButton = values(topoLayers).length < 2; 

    return (
        <Drawer anchor={drawerPosition} onClose={props.closeDrawer} open={props.open}>
            <div className={'layer-selector-container'}>
                <div className={titleClass}>
                    <h2>{l('map.layers')}</h2>
                    <IconButton onClick={props.closeDrawer} tabIndex={0} aria-label={l('uu.close')}>
                        <CloseIcon color="primary" />
                    </IconButton>
                </div>
                <div className={'layer-selector-body'}>
                    <LayerGroupWrapper title={l('layerSelector.basemap')}>
                        <div className={'image-switch-container'}>
                            <MapLayerImageButton 
                                label="Standard" 
                                layers={getBgLayerSubGroup(BgLayerSubGroup.graatone)} 
                                imgsrc="/assets/png/graatone.png" 
                            />
                            <MapLayerImageButton 
                                label={l('layerSelector.satellite')} 
                                layers={getBgLayerSubGroup(BgLayerSubGroup.orto)}
                                imgsrc={'/assets/png/nib.png'} 
                            />
                            <MapLayerImageButton 
                                label={l('layerSelector.color')} 
                                layers={topoLayers} 
                                imgsrc={'/assets/png/topo.png'} 
                            />
                            {showBasisLayerButton && <MapLayerImageButtonBasis />}
                        </div>
                    </LayerGroupWrapper>
                    <LayerGroupWrapper title={l('layerSelector.reindrift')}>
                        {layerCollectionNotEmpty(reinbeitedistriktLayer) &&
                            <div className={classes.switchButtonWrapper}>
                                <ToggleSwitch id={'layer-selector-reinbeite'} label={l('layerSelector.reinbeitedistrikt')} checked={isVisible(reinbeitedistriktLayer)} onChange={() => toggleLayers(reinbeitedistriktLayer, !isVisible(reinbeitedistriktLayer))}/>
                            </div>
                            }
                        {layerCollectionNotEmpty(beitelagsLayer) &&
                            <div className={classes.switchButtonWrapper}>
                                <ToggleSwitch id={'layer-selector-beitelag'} label={l('layerSelector.beitelag')} checked={isVisible(beitelagsLayer)} onChange={() => toggleLayers(beitelagsLayer, !isVisible(beitelagsLayer))}/>
                            </div>
                            }
                        {layerCollectionNotEmpty(samebyLayer) &&
                            <div className={classes.switchButtonWrapper}>
                                <ToggleSwitch id={'layer-selector-sameby'} label={l('layerSelector.samebygrense')} checked={isVisible(samebyLayer)} onChange={() => toggleLayers(samebyLayer, !isVisible(samebyLayer))}/>
                                <Tooltip text={l('layerSelector.samebygrense.info')} tooltipForLabel={l('layerSelector.samebygrense')}/>
                            </div>
                            }
                    </LayerGroupWrapper>
                    <LayerGroupWrapper title={l('layerSelector.managementAreas')}>
                        <MapLayerImageSwitch label={l('carnivore.wolf')} imgsrc={'/assets/svg/layerIcons/ulv.svg'}  layers={getLayerGroup(['ulv'])}/>
                        <MapLayerImageSwitch label={l('carnivore.bear')} imgsrc={'/assets/svg/layerIcons/bjorn.svg'}  layers={getLayerGroup(['bjorn'])}/>
                        <MapLayerImageSwitch label={l('carnivore.lynx')} imgsrc={'/assets/svg/layerIcons/gaupe.svg'}  layers={getLayerGroup(['gaupe'])}/>
                        <MapLayerImageSwitch label={l('carnivore.wolverine')} imgsrc={'/assets/svg/layerIcons/jerv.svg'}  layers={getLayerGroup(['jerv'])}/>
               
                    </LayerGroupWrapper>    
                </div>
            </div>
        </Drawer>
    );
}

type layerGroupProps = {
    title: string;
}

function LayerGroupWrapper(props: PropsWithChildren<layerGroupProps>): ReactElement {
    return (
        <div className={'layer-group-wrapper'}>
            <h3>{props.title}</h3>
            {props.children}
        </div>
    );
}


type ImageButtonProps = {
    label: string;
    layers: LayerCollection;
    imgsrc: string;
};


function MapLayerImageButton(props: ImageButtonProps): ReactElement {
    // Hvis ingen av kartlagene tilhørende knappen finnes i kartet så viser vi ikke knappen
    if (props.layers === undefined || values(props.layers).length === 0) {
        return null;
    }
    
    const { classes } = useLSStyles();
    const checked = isVisible(props.layers);
    const toggleLayers = useActionCreator(toggleLayerGroup);
    const checkedClass = classnames('image-button', classes.imageButton, classes.layerChecked);
    const imageButtonClass = classnames('image-button', classes.imageButton);
    const handleClickLayer = (layers: LayerCollection) => e => {
        e.preventDefault();
        if (values(layers)[0].group && checked) {
            return;
        }
        toggleLayers(layers, !isVisible(layers));
    };
    // console.log(checked);
    return (
        <button className={checked ? checkedClass : imageButtonClass } onClick={handleClickLayer(props.layers)} aria-pressed={isVisible(props.layers)}>
            <img className={'layer-image'} src={props.imgsrc} alt="" />
            <div>{props.label}</div>
        </button>
    );
}

// Denne knappen brukes kun hvis enten lantmateriet eller kartverket er skrudd av, og skal skru av de andre
// bakgrunnskartlagene i tillfelle man kun ønsker å se open street map (som ligger bakerst i kartet hele tiden)
function MapLayerImageButtonBasis(): ReactElement {
    const { classes } = useLSStyles();
    const layers = useSelector((state: ApplicationState) => state.map.layers);
    // Denne knappen er kun "på"/valgt hvis ingen andre bakgrunnskartlag vises - i praksis er osm-laget alltid på bakerst, men knappen skal kunne skru av de andre bakgrunnskartlagene
    const onlyBasisMapOn = !values(layers)
                                .filter(layer => layer.group === LayerGroup.BG)
                                .some(layer => layer.options.visible);
    const turnOffBgLayers = useActionCreator(turnOffBackgroundLayers);
    const checkedClass = classnames('image-button', classes.imageButton, classes.layerChecked);
    const imageButtonClass = classnames('image-button', classes.imageButton);
    const l = useLanguage();

    return (
        <button className={onlyBasisMapOn ? checkedClass : imageButtonClass } onClick={turnOffBgLayers} aria-pressed={onlyBasisMapOn}>
            <img className={'layer-image'} src={'/assets/png/osm.png'} alt="" />
            <div>{l('layerSelector.basis')}</div>
        </button>
    );
}

type ImageSwitchProps = {
    label: string;
    layers: LayerCollection;
    imgsrc: string;
};


function MapLayerImageSwitch(props: ImageSwitchProps): ReactElement {
    const { classes } = useLSStyles();
    const imageSwitchClass = classnames('layer-selector-image-switch', classes.imageSwitch);
    const toggleLayers = useActionCreator(toggleLayerGroup);

    return (
        <div className={imageSwitchClass}>
            <ToggleSwitch id={`map-layer-${props.label}`} label={props.label} checked={isVisible(props.layers)} onChange={() => toggleLayers(props.layers, !isVisible(props.layers))}/>
            <img src={props.imgsrc} alt={''} />
        </div>
    );
}

